/* eslint-disable react-hooks/exhaustive-deps */
// ** react imports
import React, { useEffect, useState } from 'react';

// ** router imports
import { useParams, useNavigate } from 'react-router-dom';

// ** redux imports
import { useDispatch } from 'react-redux';
import { setUserDetails } from '../../../redux/slices/users';

// ** validation imports
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// ** custom hook imports
import {
  useGetUserById,
  useUpdateUser,
  useVerifyEmailUpdate,
} from '../../../hooks/apis/useUsers/index';

// ** components imports
import Badge from '../../../components/common/Badge';
import Loading from '../../../components/common/Loading';
import NormalInput from '../../../components/common/Inputs/NormalInput';
import SelectInput from '../../../components/common/Inputs/SelectInput';
import NormalButton from '../../../components/common/Buttons/NormalButton/index';
import LoadingButton from '../../../components/common/Buttons/LoadingButton/index';
import ModalBasic from '../../../components/ModalBasic';

// ** enums imports
import { BUTTON_THEMES, USER_ROLES, SEND_SMS_VIA } from '../../../enums/common';

// ** utils imports
import { formatUtcToLocal, formatPhoneNumber } from '../../../utils/Utils';

// ** util imports
import SessionStorage from '../../../utils/SessionStorage';

// ** otp imports
import OtpInput from 'react-otp-input';

function UserPanel() {
  // hooks
  const { id } = useParams();
  const navigate = useNavigate();

  const [originalRole, setOriginalRole] = useState('');
  const [originalEmail, setOriginalEmail] = useState('');
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [emailToVerify, setEmailToVerify] = useState('');
  const [otp, setOtp] = useState('');
  const [isEmailChanged, setIsEmailChanged] = useState(false);
  const [verificationError, setVerificationError] = useState('');

  const currentUser = JSON.parse(SessionStorage.get('users') || '{}');
  const currentUserId = currentUser._id;
  const currentUserRole = currentUser.role;

  const canEditUser = (targetUserId, targetUserRole) => {
    if (currentUserRole === USER_ROLES.ADMIN) {
      return (
        targetUserRole === USER_ROLES.ADMIN ||
        targetUserRole === USER_ROLES.MANAGER
      );
    }

    if (currentUserRole === USER_ROLES.MANAGER) {
      return currentUserId === targetUserId;
    }

    return false;
  };

  const canOnlyView = (targetUserId, targetUserRole) => {
    if (currentUserRole === USER_ROLES.MANAGER) {
      return currentUserId !== targetUserId;
    }
    return false;
  };

  const canChangeRole = newRole => {
    if (currentUserRole === USER_ROLES.ADMIN) {
      return newRole === USER_ROLES.ADMIN || newRole === USER_ROLES.MANAGER;
    }
    if (currentUserRole === USER_ROLES.MANAGER) {
      return false;
    }

    return false;
  };

  // hooks
  const dispatch = useDispatch();

  // custom hooks
  const { data, isGetUserDetailsByIdLoading, getUserDetailsById } =
    useGetUserById();
  const { updateUser, isUpdateUserLoading } = useUpdateUser();
  const { verifyEmailUpdate, isVerifyEmailUpdateLoading } =
    useVerifyEmailUpdate();

  // validation schema
  const validationSchema = yup.object().shape({
    fullName: yup.string().required('Full name is required'),
    email: yup
      .string()
      .email('Invalid email format')
      .required('Email is required'),
    phone: yup.string().required('Phone number is required'),
    role: yup
      .string()
      .oneOf([USER_ROLES.ADMIN, USER_ROLES.MANAGER], 'Invalid role'),
    sendSMSVia: yup
      .string()
      .oneOf(
        [SEND_SMS_VIA.EMAIL, SEND_SMS_VIA.PHONE],
        'Invalid send SMS via option'
      ),
  });

  // react hook form
  const {
    setValue,
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  // hooks
  useEffect(() => {
    if (id) {
      getUserDetailsById(id);
    }
  }, [id]);

  useEffect(() => {
    if (Object.keys(data).length > 0) {
      setOriginalRole(data.role);
      setOriginalEmail(data.email);

      setValue('fullName', data.fullName);
      setValue('email', data.email);
      setValue('userId', data.userId);
      setValue('role', data.role);
      setValue('phone', data.phone || '');
      setValue('sendSMSVia', data.sendSMSVia || SEND_SMS_VIA.EMAIL);
    }
  }, [data]);

  // handlers
  const handleUpdateProfile = async data => {
    if (!canEditUser(id, originalRole)) {
      alert('You do not have permission to update this user profile.');
      return;
    }

    // Prepare the update data
    const updateData = {
      fullName: data.fullName,
      email: data.email,
      phone: data.phone,
    };

    if (canChangeRole(data.role)) {
      updateData.role = data.role;
    } else {
      updateData.role = originalRole;
    }

    if (currentUserRole === USER_ROLES.ADMIN) {
      updateData.sendSMSVia = data.sendSMSVia;
    }

    const response = await updateUser(id, updateData);

    if (response.data.success) {
      // Check if email has been changed
      if (data.email !== originalEmail) {
        setIsEmailChanged(true);
        setEmailToVerify(data.email);
        setShowOtpModal(true);
        return;
      }
      dispatch(setUserDetails(response.data.data));
      navigate('/users/list');
    }
  };

  const handleCancel = () => {
    navigate('/users/list');
  };

  const handleVerifyEmailChange = async () => {
    setVerificationError('');

    if (!otp || otp.length !== 6) {
      setVerificationError('Please enter a valid 6-digit OTP');
      return;
    }

    const response = await verifyEmailUpdate({
      id,
      data: { otp: Number(otp) },
    });

    if (response.data.success) {
      // Email verified successfully, now update the user
      const updateData = {
        fullName: watch('fullName'),
        email: emailToVerify,
        phone: watch('phone'),
      };

      if (canChangeRole(watch('role'))) {
        updateData.role = watch('role');
      } else {
        updateData.role = originalRole;
      }

      if (currentUserRole === USER_ROLES.ADMIN) {
        updateData.sendSMSVia = watch('sendSMSVia');
      }

      const updateUserResponse = await updateUser(id, updateData);

      if (updateUserResponse.data.success) {
        setShowOtpModal(false);
        navigate('/users/list');
      }
    }
  };

  return (
    <div className="grow">
      {isGetUserDetailsByIdLoading ? (
        <div className="min-h-[calc(50vh-100px)] bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm z-40 flex items-center justify-center rounded-xl">
          <div className="flex items-center justify-center gap-3 text-gray-600 dark:text-gray-300">
            <Loading size="medium" />
          </div>
        </div>
      ) : (
        <form
          action="POST"
          onSubmit={
            canOnlyView(data?._id, data?.role)
              ? e => {
                  e.preventDefault();
                }
              : handleSubmit(handleUpdateProfile)
          }
        >
          <div className="p-6 space-y-6">
            <div className="flex justify-between items-center">
              <div className="flex flex-col gap-2">
                <p className="text-sm text-gray-60 dark:text-gray-200">
                  <span className="font-semibold">Customer ID:</span>{' '}
                  {data?.userId}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-200">
                  <span className="font-semibold">Joined:</span>{' '}
                  {data?.createdAt ? formatUtcToLocal(data?.createdAt) : ''}
                </p>
              </div>
              <div className="flex">
                <p className="text-right text-sm text-gray-600 dark:text-gray-200">
                  <Badge
                    theme={
                      data?.isEmailVerified && data?.isPasswordSet
                        ? 'true'
                        : 'false'
                    }
                    text={
                      data?.isEmailVerified && data?.isPasswordSet
                        ? 'Verified'
                        : 'Not Verified'
                    }
                    size="sm"
                  />
                </p>
              </div>
            </div>

            {/* User Details */}
            <section>
              <div className="sm:flex sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-5">
                <div className="sm:w-1/3">
                  <NormalInput
                    label="User Name"
                    name="fullName"
                    register={register}
                    errors={errors.fullName?.message}
                    disabled={canOnlyView(data?._id, data?.role)}
                  />
                </div>
                <div className="sm:w-1/3">
                  <div className="">
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium dark:text-gray-200"
                    >
                      Phone Number
                    </label>
                    <input
                      type="text"
                      {...register('phone')}
                      value={watch('phone') || ''}
                      onChange={e => {
                        if (!canOnlyView(data?._id, data?.role)) {
                          const formattedValue = formatPhoneNumber(
                            e.target.value
                          );
                          setValue('phone', formattedValue); // Update the form state with formatted value
                        }
                      }}
                      disabled={canOnlyView(data?._id, data?.role)}
                      className={`w-full mt-1 form-input placeholder:text-sm dark:placeholder:text-gray-400 ${
                        errors.phone
                          ? 'border-red-500 focus:ring-red-500 dark:border-red-60 dark:focus:ring-red-600'
                          : ''
                      }`}
                    />
                    {errors.phone && (
                      <p className="mt-2 text-xs text-red-500">
                        {errors.phone.message}
                      </p>
                    )}
                  </div>
                </div>
                <div className="sm:w-1/3">
                  <NormalInput
                    label="Email Address"
                    name="email"
                    register={register}
                    errors={errors.email?.message}
                    disabled={canOnlyView(data?._id, data?.role)}
                  />
                </div>
              </div>
            </section>

            <section>
              <div className="sm:flex sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-5">
                <div className="sm:w-1/3">
                  <SelectInput
                    label="Role"
                    name="role"
                    options={[
                      { label: 'Admin', value: 'admin' },
                      { label: 'Manager', value: 'manager' },
                    ]}
                    register={register}
                    errors={errors.role?.message}
                    disabled={
                      !canChangeRole(data?.role) ||
                      canOnlyView(data?._id, data?.role)
                    }
                  />
                </div>
                <div className="sm:w-1/3">
                  {currentUserRole === USER_ROLES.ADMIN && (
                    <SelectInput
                      label="Send SMS Via"
                      name="sendSMSVia"
                      options={[
                        { label: 'Email', value: SEND_SMS_VIA.EMAIL },
                        { label: 'Phone', value: SEND_SMS_VIA.PHONE },
                      ]}
                      register={register}
                      errors={errors.sendSMSVia?.message}
                      disabled={canOnlyView(data?._id, data?.role)}
                    />
                  )}
                </div>
                <div className="sm:w-1/3"></div>
              </div>
            </section>
          </div>

          <footer>
            {!canOnlyView(data?._id, data?.role) && (
              <div className="flex flex-col px-6 py-5 border-t border-gray-200 dark:border-gray-700/60">
                <div className="flex self-end gap-2">
                  <NormalButton
                    type="button"
                    theme={BUTTON_THEMES.DANGER}
                    onClick={handleCancel}
                  >
                    Cancel
                  </NormalButton>
                  <LoadingButton
                    type="submit"
                    theme={BUTTON_THEMES.PRIMARY}
                    isLoading={isUpdateUserLoading}
                  >
                    Save Changes
                  </LoadingButton>
                </div>
              </div>
            )}
          </footer>
        </form>
      )}

      {/* OTP Verification Modal */}
      <ModalBasic
        title="Email Verification"
        modalOpen={showOtpModal}
        setModalOpen={setShowOtpModal}
      >
        <div className="p-5">
          <div className="mb-4">
            <p className="text-sm text-gray-800 dark:text-gray-100">
              An email verification is required for changing the email address.
              Please enter the OTP sent to{' '}
              <span className="font-bold">{emailToVerify}</span>.
            </p>
          </div>

          <div className="mb-4">
            <OtpInput
              containerStyle={{
                width: '100%',
                justifyContent: 'center',
              }}
              inputStyle={{
                width: '100%',
                maxWidth: '40px',
                height: '40px',
                margin: '0 5px',
                textAlign: 'center',
                borderRadius: '5px',
                border: '1px solid #e5e7eb',
                fontSize: '16px',
                backgroundColor: '#ffffff',
              }}
              value={otp}
              onChange={setOtp}
              numInputs={6}
              inputType="tel"
              renderSeparator={<span style={{ margin: '0 5px' }}>-</span>}
              renderInput={props => <input {...props} />}
            />
            {verificationError && (
              <p className="ml-12 mt-2 text-xs font-light text-red-700">
                {verificationError}
              </p>
            )}
          </div>

          <div className="flex justify-end items-center mt-6">
            <div className="flex gap-2">
              <NormalButton
                type="button"
                theme={BUTTON_THEMES.DANGER}
                onClick={() => setShowOtpModal(false)}
              >
                Cancel
              </NormalButton>
              <LoadingButton
                type="button"
                theme={BUTTON_THEMES.PRIMARY}
                isLoading={isVerifyEmailUpdateLoading}
                onClick={handleVerifyEmailChange}
              >
                Verify
              </LoadingButton>
            </div>
          </div>
        </div>
      </ModalBasic>
    </div>
  );
}

export default UserPanel;
